import {useGetInfinitePatientsTherapiesSequences} from '@/modules/patients-therapies/api'
import {PatientsTherapiesFiltersSchema} from '@/modules/patients-therapies/schemas'
import {ProductionTherapiesSequencesTable} from '@/modules/production/components/patients-therapies-table'
import {ProductionSearch} from '@/modules/production/production-search'
import {ProductionSearchSchema} from '@/modules/production/schemas'
import {useAppForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {OnChangeFn, SortingState} from '@tanstack/react-table'
import {useCallback} from 'react'

const LIMIT = 50

export const Route = createFileRoute('/production/')({
  component: RouteComponent,
  validateSearch: ProductionSearchSchema,
})

function RouteComponent() {
  const navigate = useNavigate()
  const search = Route.useSearch()

  const form = useAppForm({
    defaultValues: {
      search: search.filters?.search || '',
      productionStatus: search.filters?.productionStatus || 'validation_queue',
      wards: search.filters?.wards || [],
      sections: search.filters?.sections || [],
      responsibleDoctors: search.filters?.responsibleDoctors || [],
      therapyDate: search.filters?.therapyDate || [],
    } as PatientsTherapiesFiltersSchema,
    listeners: {
      onChange({formApi, fieldApi}) {
        if (fieldApi.name === 'search') return
        const {values} = formApi.state
        navigate({
          from: '/production/',
          to: '/production',
          search: prev => ({
            ...prev,
            filters: {
              search: values.search,
              wards: values.wards,
              sections: values.sections,
              productionStatus: values.productionStatus,
              responsibleDoctors: values.responsibleDoctors,
              therapyDate: values.therapyDate?.map(date => date.toString()) || [],
            },
          }),
        })
      },
    },
  })
  const productionStatus = useStore(form.store, state => state.values.productionStatus)

  const [sort] = search.sort

  const infinite = useGetInfinitePatientsTherapiesSequences({
    filters: search.filters,
    limit: LIMIT,
    sort: {
      dir: sort?.desc ? 'desc' : 'asc',
      field: sort?.id || 'createdAt',
    },
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infinite

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    updaterOrValue => {
      const newValue = typeof updaterOrValue === 'function' ? updaterOrValue(search.sort) : updaterOrValue
      navigate({to: '.', search: prev => ({...prev, sort: newValue})})
    },
    [navigate, search.sort]
  )

  function onClearFilters() {
    form.setFieldValue('search', '')
    form.setFieldValue('wards', [])
    form.setFieldValue('sections', [])
    form.setFieldValue('therapyDate', [])
    form.setFieldValue('responsibleDoctors', [])
  }
  const hasValues = useStore(form.store, state =>
    Boolean(
      state.values?.search ||
      state.values?.wards?.length ||
      state.values?.sections?.length ||
      state.values?.therapyDate?.length ||
      state.values?.responsibleDoctors?.length
    )
  )

  return (
    <>
      <ProductionSearch form={form} hasValues={hasValues} onClearFilters={onClearFilters} />
      <ProductionTherapiesSequencesTable
        virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
        table={{onSortingChange, state: {sorting: search.sort}}}
        productionStatus={productionStatus}
        dateFilterActive={Boolean(search.filters?.therapyDate?.length)}
      />
    </>
  )
}
